import { TableCell, TableRow, Tooltip } from '@mui/material'
import type { TableCellProps } from '@mui/material/TableCell'
import { CancelSharp, CheckCircleSharp, Help } from '@mui/icons-material'
import { isValidElement } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  Composition,
  ComparableDeviceCompositionKey,
} from '@/pages/calibration/composition'

const SUCCESS_COLOR = '#4caf50'

// Configuration values from the schema are always string | string[].
// The function form of the renderer receives that actual value.
type ConfigurationRenderer =
  JSX.Element | ((value: string | string[]) => JSX.Element)

interface Props {
  valueKey: ComparableDeviceCompositionKey
  composition: Composition
  compositionRenderer?: (value: string) => JSX.Element
  compositionCellProps?: TableCellProps
  configurationRenderer?: ConfigurationRenderer
  configurationCellProps?: TableCellProps
}

const renderConfigurationValue = (
  value: string | string[],
  renderer?: ConfigurationRenderer,
): JSX.Element | string | null => {
  if (!renderer) {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return 'Not configured'
      }
      return (
        <>
          {value.map((v) => (
            <div key={v}>{v}</div>
          ))}
        </>
      )
    }
    return value
  }

  if (isValidElement(renderer)) {
    return renderer
  }

  if (typeof renderer === 'function') {
    return renderer(value)
  }

  return null
}

export const CompositionCheckRow = ({
  valueKey,
  composition,
  compositionRenderer,
  configurationRenderer,
  compositionCellProps = {},
  configurationCellProps = {},
}: Props): JSX.Element | null => {
  const { t } = useTranslation('app')

  const { composition: comp, configuration, mismatches } = composition
  const compositionValue = comp[valueKey]
  const configurationValue = configuration[valueKey]

  if (
    Array.isArray(configurationValue) &&
    configurationValue.length === 0 &&
    valueKey !== 'rdpNmdi'
  ) {
    return null
  }

  const hasPercentSign = Array.isArray(configurationValue)
    ? configurationValue.some((v) => typeof v === 'string' && v.includes('%'))
    : typeof configurationValue === 'string' && configurationValue.includes('%')

  const isValid = !mismatches.includes(valueKey)

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {t(`device.calibration.preFlightStep.check.${valueKey}`)}
          {hasPercentSign && (
            <Tooltip
              title={t('device.calibration.preFlightStep.explanation')}
              arrow
            >
              <Help
                color="action"
                sx={{ float: 'right' }}
                aria-label="percent-explanation"
              />
            </Tooltip>
          )}
        </div>
      </TableCell>

      <TableCell align="right" sx={{ pt: '20px' }}>
        {isValid ? (
          <CheckCircleSharp
            color="primary"
            sx={{ color: SUCCESS_COLOR }}
            aria-label="valid"
          />
        ) : (
          <CancelSharp color="error" aria-label="invalid" />
        )}
      </TableCell>

      <TableCell align="right" {...compositionCellProps}>
        {compositionRenderer
          ? compositionRenderer(compositionValue)
          : compositionValue}
      </TableCell>

      <TableCell align="right" {...configurationCellProps}>
        {renderConfigurationValue(
          configurationValue as string | string[],
          configurationRenderer,
        )}
      </TableCell>
    </TableRow>
  )
}
